import {
  compareInstants,
  shiftEditorDraftSchema,
  type BuildMetadata,
  type JsonValue,
  type Location,
  type RouteTemplate,
  type RouteVariant,
  type ShiftAggregate,
  type Vehicle,
} from '@der-maschinfuehrer/contracts';
import {
  DraftRevisionConflictError,
  LocalReplacementCommittedError,
  ProductionRollbackCompatibilityError,
  RevisionConflictError,
  openOrCreateAnonymousLocalDataStore,
  type ApplyRouteTemplateToDraftInput,
  type ApplyRouteTemplateToDraftResult,
  type DraftRecord,
  type LocalDataStore,
  type RouteTemplateMutation,
  type ShiftDraftBundleMutation,
  type ShiftDraftBundleResult,
  type ShiftMutation,
  type UndoLatestSegmentSaveInput,
} from '@der-maschinfuehrer/local-db';

import {
  backupMatchesCurrentPayload,
  createLocalReplaceExecution,
  createLocalRestorePreview,
  jsonBackupCounts,
  parseBoundedJsonBackup,
  prepareJsonBackup,
  restorePreviewForUi,
  type CreatedJsonBackup,
  type CurrentJsonBackupVerification,
  type JsonRestorePreview,
  type JsonRestoreResult,
} from './json-backup';

export interface AppDraftVersion {
  readonly draft_id: string;
  readonly autosave_revision: number;
}

export class AppDraftConflictError extends Error {
  public readonly code = 'draft_revision_conflict' as const;

  public constructor(
    public readonly draft_key: string,
    public readonly expected_version: AppDraftVersion | null,
    public readonly actual_version: AppDraftVersion | null,
    cause: DraftRevisionConflictError,
  ) {
    super(`Draft ${draft_key} changed in another editor.`, { cause });
    this.name = new.target.name;
  }
}

export class AppShiftConflictError extends Error {
  public readonly code = 'shift_revision_conflict' as const;

  public constructor(
    public readonly shift_id: string,
    public readonly expected_revision: number | null,
    public readonly actual_revision: number | null,
    cause: RevisionConflictError,
  ) {
    super(`Shift ${shift_id} changed in another editor.`, { cause });
    this.name = new.target.name;
  }
}

export class AppSegmentConflictError extends Error {
  public readonly code = 'segment_revision_conflict' as const;

  public constructor(
    public readonly trip_segment_id: string,
    public readonly expected_revision: number | null,
    public readonly actual_revision: number | null,
    cause: RevisionConflictError,
  ) {
    super(`Segment ${trip_segment_id} changed in another editor.`, { cause });
    this.name = new.target.name;
  }
}

export type AppSupportingEntityType =
  'route_template' | 'route_variant' | 'user_defined_location' | 'vehicle';

export class AppEntityConflictError extends Error {
  public readonly code = 'entity_revision_conflict' as const;

  public constructor(
    public readonly entity_type: AppSupportingEntityType,
    public readonly entity_id: string,
    public readonly expected_revision: number | null,
    public readonly actual_revision: number | null,
    cause: RevisionConflictError,
  ) {
    super(`${entity_type} ${entity_id} changed in another editor.`, { cause });
    this.name = new.target.name;
  }
}

export class AppProductionRollbackCompatibilityError extends Error {
  public readonly code = 'production_rollback_compatibility' as const;

  public constructor(cause: Error) {
    super(
      'Tuto změnu teď nelze bezpečně uložit, protože by mohla porušit kompatibilitu uložené výchozí trasy. Uložená data zůstala beze změny; po dokončení aktualizace lze změnu zopakovat.',
      { cause },
    );
    this.name = new.target.name;
  }
}

export interface AppDraftSave {
  readonly draft_key: string;
  readonly payload: JsonValue;
  readonly expected_draft_version: AppDraftVersion | null;
}

export interface AppShiftDraftCommit {
  readonly draft_key: string;
  readonly mutation: ShiftMutation;
  readonly expected_revision: number | null;
  readonly expected_draft_version: AppDraftVersion;
}

export interface AppShiftDraftBundleCommit {
  readonly draft_key: string;
  readonly mutation: ShiftDraftBundleMutation;
  readonly expected_draft_version: AppDraftVersion;
}

export interface AppStorage {
  readonly localProfileId: string;
  readonly datasetEpoch: string;
  getShift(shiftId: string): Promise<ShiftAggregate | null>;
  listShifts(): Promise<readonly ShiftAggregate[]>;
  getUserDefinedLocation(locationId: string): Promise<Location | null>;
  listUserDefinedLocations(): Promise<readonly Location[]>;
  getRouteVariant(routeVariantId: string): Promise<RouteVariant | null>;
  listRouteVariants(options?: {
    readonly include_deleted?: boolean;
  }): Promise<readonly RouteVariant[]>;
  saveRouteVariantPreferences(input: {
    readonly route_variant_id: string;
    readonly favorite: boolean;
    readonly expected_revision: number;
  }): Promise<RouteVariant>;
  getRouteTemplate(routeTemplateId: string): Promise<RouteTemplate | null>;
  listRouteTemplates(): Promise<readonly RouteTemplate[]>;
  saveRouteTemplate(mutation: RouteTemplateMutation): Promise<RouteTemplate>;
  applyRouteTemplateToDraft(
    input: ApplyRouteTemplateToDraftInput,
  ): Promise<ApplyRouteTemplateToDraftResult>;
  getVehicle(vehicleId: string): Promise<Vehicle | null>;
  listVehicles(): Promise<readonly Vehicle[]>;
  getDraft(draftKey: string): Promise<DraftRecord | null>;
  autosaveDraft(input: AppDraftSave): Promise<DraftRecord>;
  discardDraft(draftKey: string, expectedDraftVersion: AppDraftVersion): Promise<void>;
  saveShift(mutation: ShiftMutation, expectedRevision: number | null): Promise<ShiftAggregate>;
  commitShiftDraft(input: AppShiftDraftCommit): Promise<ShiftAggregate>;
  commitShiftDraftBundle(input: AppShiftDraftBundleCommit): Promise<ShiftDraftBundleResult>;
  undoLatestSegmentSave(input: UndoLatestSegmentSaveInput): Promise<ShiftAggregate>;
  createJsonBackup(buildMetadata: BuildMetadata): Promise<CreatedJsonBackup>;
  verifyCurrentJsonBackup(bytes: Uint8Array): Promise<CurrentJsonBackupVerification>;
  previewJsonRestore(bytes: Uint8Array): Promise<JsonRestorePreview>;
  restoreJsonBackup(input: {
    readonly expected_whole_file_sha256: string;
    readonly reselected_bytes: Uint8Array;
    readonly explicit_confirmation: true;
  }): Promise<JsonRestoreResult>;
  flushPendingWrites(): Promise<void>;
  close(): void;
}

type WriteOutcome = { readonly ok: true } | { readonly error: unknown; readonly ok: false };

class RepositoryAppStorage implements AppStorage {
  readonly #activeWriteOutcomes = new Map<number, Promise<WriteOutcome>>();
  readonly #draftQueues = new Map<string, Promise<void>>();
  #datasetEpoch: string;
  readonly #restorePreviews = new Map<
    string,
    {
      readonly covered_mutation_generation: string;
      readonly source_export_id: string;
      readonly target_dataset_epoch: string;
    }
  >();
  #restorePreviewRequest = Symbol('initial restore preview state');
  #store: LocalDataStore;
  readonly #unconsumedWriteFailures = new Map<number, unknown>();
  #writeSequence = 0;

  public constructor(
    readonly localProfileId: string,
    datasetEpoch: string,
    store: LocalDataStore,
  ) {
    this.#datasetEpoch = datasetEpoch;
    this.#store = store;
  }

  public get datasetEpoch(): string {
    return this.#datasetEpoch;
  }

  public getShift(shiftId: string): Promise<ShiftAggregate | null> {
    return this.#store.getShiftAggregate(shiftId);
  }

  public async listShifts(): Promise<readonly ShiftAggregate[]> {
    const shifts = await this.#store.listShiftAggregates();
    return shifts.sort(
      (left, right) =>
        compareAscii(right.shift.service_date, left.shift.service_date) ||
        compareInstants(right.shift.metadata.updated_at, left.shift.metadata.updated_at) ||
        compareAscii(left.shift.shift_id, right.shift.shift_id),
    );
  }

  public getUserDefinedLocation(locationId: string): Promise<Location | null> {
    return this.#store.getUserDefinedLocation(locationId);
  }

  public listUserDefinedLocations(): Promise<readonly Location[]> {
    return this.#store.listUserDefinedLocations();
  }

  public getRouteVariant(routeVariantId: string): Promise<RouteVariant | null> {
    return this.#store.getRouteVariant(routeVariantId);
  }

  public listRouteVariants(
    options: { readonly include_deleted?: boolean } = {},
  ): Promise<readonly RouteVariant[]> {
    return this.#store.listRouteVariants(options);
  }

  public async saveRouteVariantPreferences(input: {
    readonly route_variant_id: string;
    readonly favorite: boolean;
    readonly expected_revision: number;
  }): Promise<RouteVariant> {
    return this.#trackWrite(async () => {
      try {
        return await this.#store.saveRouteVariantPreferences(input);
      } catch (error) {
        translateRepositoryConflict(error, {
          supporting_entity_types: new Map<string, AppSupportingEntityType>([
            [input.route_variant_id, 'route_variant'],
          ]),
        });
      }
    });
  }

  public getRouteTemplate(routeTemplateId: string): Promise<RouteTemplate | null> {
    return this.#store.getRouteTemplate(routeTemplateId);
  }

  public listRouteTemplates(): Promise<readonly RouteTemplate[]> {
    return this.#store.listRouteTemplates();
  }

  public async saveRouteTemplate(mutation: RouteTemplateMutation): Promise<RouteTemplate> {
    return this.#trackWrite(async () => {
      try {
        return await this.#store.saveRouteTemplate(mutation);
      } catch (error) {
        translateRepositoryConflict(error, {
          supporting_entity_types: new Map<string, AppSupportingEntityType>([
            [mutation.value.route_template_id, 'route_template'],
          ]),
        });
      }
    });
  }

  public async applyRouteTemplateToDraft(
    input: ApplyRouteTemplateToDraftInput,
  ): Promise<ApplyRouteTemplateToDraftResult> {
    const draft = shiftEditorDraftSchema.parse(input.draft.payload);
    return this.#trackWrite(async () => {
      try {
        return await this.#runDraftOperation(input.draft.draft_key, () =>
          this.#store.applyRouteTemplateToDraft(input),
        );
      } catch (error) {
        translateRepositoryConflict(error, {
          shift_id: draft.shift_id,
          supporting_entity_types: new Map<string, AppSupportingEntityType>([
            [input.template.route_template_id, 'route_template'],
          ]),
        });
      }
    });
  }

  public getVehicle(vehicleId: string): Promise<Vehicle | null> {
    return this.#store.getVehicle(vehicleId);
  }

  public listVehicles(): Promise<readonly Vehicle[]> {
    return this.#store.listVehicles();
  }

  public getDraft(draftKey: string): Promise<DraftRecord | null> {
    return this.#store.getDraft(draftKey);
  }

  public async autosaveDraft(input: AppDraftSave): Promise<DraftRecord> {
    return this.#trackWrite(async () => {
      try {
        return await this.#runDraftOperation(input.draft_key, () =>
          this.#store.autosaveDraft({
            draft_key: input.draft_key,
            draft_kind: 'shift_editor',
            payload: input.payload,
            expected_draft_version: input.expected_draft_version,
          }),
        );
      } catch (error) {
        translateRepositoryConflict(error);
      }
    });
  }

  public async discardDraft(
    draftKey: string,
    expectedDraftVersion: AppDraftVersion,
  ): Promise<void> {
    return this.#trackWrite(async () => {
      try {
        await this.#runDraftOperation(draftKey, () =>
          this.#store.discardDraft(draftKey, expectedDraftVersion),
        );
      } catch (error) {
        translateRepositoryConflict(error);
      }
    });
  }

  public async saveShift(
    mutation: ShiftMutation,
    expectedRevision: number | null,
  ): Promise<ShiftAggregate> {
    return this.#trackWrite(async () => {
      try {
        return await this.#store.saveShiftAggregate(mutation, {
          expected_revision: expectedRevision,
        });
      } catch (error) {
        translateRepositoryConflict(error, { shift_id: mutation.shift.shift_id });
      }
    });
  }

  public async commitShiftDraft(input: AppShiftDraftCommit): Promise<ShiftAggregate> {
    return this.#trackWrite(async () => {
      try {
        return await this.#runDraftOperation(input.draft_key, () =>
          this.#store.commitShiftDraft(input.draft_key, input.mutation, {
            expected_revision: input.expected_revision,
            expected_draft_version: input.expected_draft_version,
          }),
        );
      } catch (error) {
        translateRepositoryConflict(error, { shift_id: input.mutation.shift.shift_id });
      }
    });
  }

  public async commitShiftDraftBundle(
    input: AppShiftDraftBundleCommit,
  ): Promise<ShiftDraftBundleResult> {
    return this.#trackWrite(async () => {
      try {
        return await this.#runDraftOperation(input.draft_key, () =>
          this.#store.commitShiftDraftBundle(input.draft_key, input.mutation, {
            expected_draft_version: input.expected_draft_version,
          }),
        );
      } catch (error) {
        const supportingEntityTypes = new Map<string, AppSupportingEntityType>(
          input.mutation.user_defined_locations.map(
            ({ value }): [string, AppSupportingEntityType] => [
              value.location_id,
              'user_defined_location',
            ],
          ),
        );
        if (input.mutation.route_variant !== null) {
          supportingEntityTypes.set(
            input.mutation.route_variant.value.route_variant_id,
            'route_variant',
          );
        }
        for (const { value } of input.mutation.vehicles) {
          supportingEntityTypes.set(value.vehicle_id, 'vehicle');
        }
        translateRepositoryConflict(error, {
          shift_id: input.mutation.shift_aggregate.value.shift.shift_id,
          supporting_entity_types: supportingEntityTypes,
        });
      }
    });
  }

  public async undoLatestSegmentSave(input: UndoLatestSegmentSaveInput): Promise<ShiftAggregate> {
    return this.#trackWrite(async () => {
      try {
        return await this.#store.undoLatestSegmentSave(input);
      } catch (error) {
        translateRepositoryConflict(error, {
          shift_id: input.shift_id,
          trip_segment_id: input.trip_segment_id,
        });
      }
    });
  }

  public async createJsonBackup(buildMetadata: BuildMetadata): Promise<CreatedJsonBackup> {
    await this.flushPendingWrites();
    const snapshot = await this.#store.createExportPayload();
    const prepared = prepareJsonBackup({
      build_metadata: buildMetadata,
      now: new Date().toISOString(),
      payload_snapshot: snapshot,
      uuid: newUuid(),
    });
    return {
      canonical_json: prepared.canonical_json,
      counts: prepared.counts,
      export_id: prepared.export_id,
      exported_at: prepared.exported_at,
      file_name: prepared.file_name,
      whole_file_sha256: prepared.whole_file_sha256,
    };
  }

  public async verifyCurrentJsonBackup(bytes: Uint8Array): Promise<CurrentJsonBackupVerification> {
    await this.flushPendingWrites();
    const verifiedFile = parseBoundedJsonBackup(bytes);
    this.#store.validateLocalReplacementSource(verifiedFile.envelope.payload);
    const snapshot = await this.#store.createExportPayload();
    const matchesCurrentData = backupMatchesCurrentPayload(verifiedFile, snapshot.payload);
    if (!matchesCurrentData) {
      return {
        counts: jsonBackupCounts(verifiedFile.envelope.payload),
        export_id: verifiedFile.envelope.export_id,
        issues: [
          {
            code: 'backup_not_current',
            message: 'Vybraný soubor neodpovídá aktuálním uloženým datům tohoto lokálního profilu.',
            severity: 'error',
          },
        ],
        matches_current_data: false,
        whole_file_sha256: verifiedFile.whole_file_sha256,
      };
    }
    await this.#trackWrite(
      () =>
        this.#store.recordVerifiedLocalExport({
          verified_export: {
            export_id: verifiedFile.envelope.export_id,
            local_profile_id: this.localProfileId,
            dataset_epoch: this.datasetEpoch,
            export_sha256: verifiedFile.whole_file_sha256,
            schema_version: verifiedFile.envelope.schema_version,
            verified_at: new Date().toISOString(),
          },
          covered_mutation_generation: snapshot.covered_mutation_generation,
        }),
      true,
    );
    return {
      counts: jsonBackupCounts(verifiedFile.envelope.payload),
      export_id: verifiedFile.envelope.export_id,
      issues: [],
      matches_current_data: true,
      whole_file_sha256: verifiedFile.whole_file_sha256,
    };
  }

  public async previewJsonRestore(bytes: Uint8Array): Promise<JsonRestorePreview> {
    const request = Symbol('restore preview request');
    this.#restorePreviewRequest = request;
    this.#restorePreviews.clear();
    await this.flushPendingWrites();
    const verifiedFile = parseBoundedJsonBackup(bytes);
    this.#store.validateLocalReplacementSource(verifiedFile.envelope.payload);
    const snapshot = await this.#store.createExportPayload();
    if (snapshot.payload.local_profile.account_identity !== null) {
      throw new Error('Lokální obnova je povolena pouze pro profil bez cloudového účtu.');
    }
    const preview = createLocalRestorePreview(verifiedFile, snapshot.payload);
    if (this.#restorePreviewRequest !== request) {
      throw new Error('Novější výběr souboru zneplatnil tento náhled obnovy.');
    }
    this.#restorePreviews.set(verifiedFile.whole_file_sha256, {
      covered_mutation_generation: snapshot.covered_mutation_generation,
      source_export_id: verifiedFile.envelope.export_id,
      target_dataset_epoch: snapshot.payload.dataset_epoch.dataset_epoch,
    });
    return restorePreviewForUi(verifiedFile, preview);
  }

  public async restoreJsonBackup(input: {
    readonly expected_whole_file_sha256: string;
    readonly reselected_bytes: Uint8Array;
    readonly explicit_confirmation: true;
  }): Promise<JsonRestoreResult> {
    if (input.explicit_confirmation !== true) {
      throw new Error('Obnova vyžaduje výslovné potvrzení nahrazení lokálních dat.');
    }
    const preparedPreview = this.#restorePreviews.get(input.expected_whole_file_sha256);
    this.#restorePreviewRequest = Symbol('restore execution invalidation');
    this.#restorePreviews.clear();
    await this.flushPendingWrites();
    const verifiedFile = parseBoundedJsonBackup(input.reselected_bytes);
    if (verifiedFile.whole_file_sha256 !== input.expected_whole_file_sha256) {
      throw new Error('Znovu vybraný soubor neodpovídá souboru z náhledu.');
    }
    if (
      preparedPreview === undefined ||
      preparedPreview.source_export_id !== verifiedFile.envelope.export_id
    ) {
      throw new Error('Před obnovou je nutné znovu vytvořit náhled vybraného souboru.');
    }
    const currentSnapshot = await this.#store.createExportPayload();
    if (
      preparedPreview.target_dataset_epoch !==
        currentSnapshot.payload.dataset_epoch.dataset_epoch ||
      preparedPreview.covered_mutation_generation !== currentSnapshot.covered_mutation_generation
    ) {
      throw new Error('Lokální data se od náhledu změnila. Vytvoř nový náhled obnovy.');
    }

    const checkpoint = await this.#trackWrite(
      () =>
        this.#store.createReplacementCheckpoint({
          expected_mutation_generation: preparedPreview.covered_mutation_generation,
        }),
      true,
    );
    const blockedEpochs = new Set([
      currentSnapshot.payload.dataset_epoch.dataset_epoch,
      verifiedFile.envelope.payload.dataset_epoch.dataset_epoch,
    ]);
    const execution = createLocalReplaceExecution({
      checkpoint,
      current_payload: currentSnapshot.payload,
      expected_whole_file_sha256: input.expected_whole_file_sha256,
      import_plan_id: newUuid(),
      now: new Date().toISOString(),
      replacement_dataset_epoch: newDistinctUuid(blockedEpochs),
      verified_file: verifiedFile,
    });
    let reopenRequired = false;
    try {
      await this.#trackWrite(
        () =>
          this.#store.replaceLocalDatasetEpoch({
            authorization: execution.authorization,
            dataset_epoch: execution.dataset_epoch,
            source_payload: execution.source_payload,
          }),
        true,
      );
      this.#datasetEpoch = execution.dataset_epoch.dataset_epoch;
    } catch (error) {
      if (!(error instanceof LocalReplacementCommittedError)) {
        throw error;
      }
      this.#datasetEpoch = error.replacement_epoch;
      reopenRequired = !(await this.#recoverCommittedReplacement(error));
    }
    this.#restorePreviews.clear();
    return {
      reopen_required: reopenRequired,
      replacement_dataset_epoch: this.#datasetEpoch,
    };
  }

  public async flushPendingWrites(): Promise<void> {
    let observedThrough = this.#writeSequence;
    for (;;) {
      const activeOutcomes = [...this.#activeWriteOutcomes.entries()]
        .filter(([sequence]) => sequence <= observedThrough)
        .map(([, outcome]) => outcome);
      await Promise.all(activeOutcomes);

      if (this.#writeSequence > observedThrough) {
        observedThrough = this.#writeSequence;
        continue;
      }

      const failures = [...this.#unconsumedWriteFailures.entries()]
        .filter(([sequence]) => sequence <= observedThrough)
        .sort(([left], [right]) => left - right);
      for (const [sequence] of failures) {
        this.#unconsumedWriteFailures.delete(sequence);
      }
      const firstFailure = failures[0];
      if (firstFailure !== undefined) {
        throw firstFailure[1];
      }
      return;
    }
  }

  public close(): void {
    this.#restorePreviewRequest = Symbol('closed restore preview state');
    this.#restorePreviews.clear();
    this.#store.close();
  }

  async #runDraftOperation<T>(draftKey: string, operation: () => Promise<T>): Promise<T> {
    const predecessor = this.#draftQueues.get(draftKey) ?? Promise.resolve();
    const result = predecessor.catch(() => undefined).then(operation);
    const tail = result.then(
      () => undefined,
      () => undefined,
    );
    this.#draftQueues.set(draftKey, tail);
    try {
      return await result;
    } finally {
      if (this.#draftQueues.get(draftKey) === tail) {
        this.#draftQueues.delete(draftKey);
      }
    }
  }

  async #recoverCommittedReplacement(error: LocalReplacementCommittedError): Promise<boolean> {
    let recovered: LocalDataStore | null = null;
    try {
      recovered = await openOrCreateAnonymousLocalDataStore();
      const profile = await recovered.profile();
      if (
        profile.local_profile_id !== this.localProfileId ||
        profile.dataset_epoch !== error.replacement_epoch
      ) {
        throw new Error('Obnovené úložiště neodpovídá potvrzenému nahrazení dat.');
      }
      this.#store = recovered;
      recovered = null;
      return true;
    } catch {
      return false;
    } finally {
      recovered?.close();
    }
  }

  #trackWrite<T>(operation: () => Promise<T>, failureObservedByCaller = false): Promise<T> {
    const sequence = ++this.#writeSequence;
    const result = operation();
    const tracking = { failureObservedByCaller: false };
    const outcome = result.then<WriteOutcome, WriteOutcome>(
      () => ({ ok: true }),
      (error: unknown) => ({ error, ok: false }),
    );
    this.#activeWriteOutcomes.set(sequence, outcome);
    void outcome.then((settled) => {
      this.#activeWriteOutcomes.delete(sequence);
      if (!settled.ok && !tracking.failureObservedByCaller) {
        this.#unconsumedWriteFailures.set(sequence, settled.error);
      }
    });
    if (!failureObservedByCaller) {
      return result;
    }
    return result.catch((error: unknown) => {
      tracking.failureObservedByCaller = true;
      this.#unconsumedWriteFailures.delete(sequence);
      throw error;
    });
  }
}

interface RepositoryConflictContext {
  readonly shift_id?: string;
  readonly trip_segment_id?: string;
  readonly supporting_entity_types?: ReadonlyMap<string, AppSupportingEntityType>;
}

function translateRepositoryConflict(
  error: unknown,
  context: RepositoryConflictContext = {},
): never {
  if (error instanceof ProductionRollbackCompatibilityError) {
    throw new AppProductionRollbackCompatibilityError(error);
  }
  if (error instanceof DraftRevisionConflictError) {
    throw new AppDraftConflictError(
      error.draft_key,
      error.expected_version,
      error.actual_version,
      error,
    );
  }
  if (error instanceof RevisionConflictError) {
    if (error.entity_id === context.shift_id) {
      throw new AppShiftConflictError(
        error.entity_id,
        error.expected_revision,
        error.actual_revision,
        error,
      );
    }
    if (error.entity_id === context.trip_segment_id) {
      throw new AppSegmentConflictError(
        error.entity_id,
        error.expected_revision,
        error.actual_revision,
        error,
      );
    }
    const supportingEntityType = context.supporting_entity_types?.get(error.entity_id);
    if (supportingEntityType !== undefined) {
      throw new AppEntityConflictError(
        supportingEntityType,
        error.entity_id,
        error.expected_revision,
        error.actual_revision,
        error,
      );
    }
  }
  throw error;
}

function compareAscii(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function newUuid(): string {
  return globalThis.crypto.randomUUID();
}

function newDistinctUuid(blocked: ReadonlySet<string>): string {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const candidate = newUuid();
    if (!blocked.has(candidate)) {
      return candidate;
    }
  }
  throw new Error('Nepodařilo se vytvořit bezpečnou identitu nové datové sady.');
}

export async function openAppStorage(): Promise<AppStorage> {
  const store = await openOrCreateAnonymousLocalDataStore();
  try {
    const profile = await store.profile();
    return new RepositoryAppStorage(profile.local_profile_id, profile.dataset_epoch, store);
  } catch (error) {
    store.close();
    throw error;
  }
}
